<template>
  <div class="card h-100 provider-card shadow-sm">
    <!-- Header -->
    <div class="provider-card__header">
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <span class="provider-card__dot" :class="`dot-${badge.tone}`"></span>
        <h5 class="mb-0 font-weight-bold">{{ providerLabel }}</h5>
      </div>
      <b-badge :variant="badge.variant" class="provider-card__status-badge" pill>
        {{ badge.text }}
      </b-badge>
    </div>

    <div class="provider-card__body">
      <!-- Top-level non-field errors -->
      <div v-if="serverNonFieldError" class="alert alert-danger py-2 mb-3" role="alert">
        <i class="material-icons icon-16pt mr-1">error_outline</i>
        {{ serverNonFieldError }}
      </div>

      <!-- API Key -->
      <section class="provider-section">
        <label class="provider-section__label">API key</label>
        <b-input-group size="sm">
          <b-form-input
            type="password"
            :placeholder="apiKeyPlaceholder"
            v-model="form.api_key"
            autocomplete="new-password"
            :state="fieldErrors.api_key ? false : null"
          />
          <b-input-group-append v-if="form.api_key">
            <b-button variant="outline-secondary" @click="form.api_key = ''">Clear</b-button>
          </b-input-group-append>
        </b-input-group>
        <small v-if="fieldErrors.api_key" class="text-danger d-block mt-1">{{ fieldErrors.api_key }}</small>
        <small v-else class="provider-help">
          Leave blank to keep the existing key. The full secret is never shown.
        </small>
      </section>

      <!-- Models -->
      <section class="provider-section">
        <label class="provider-section__label">Models</label>
        <div class="provider-grid provider-grid--2">
          <div>
            <span class="provider-mini-label">Default</span>
            <b-form-select
              size="sm"
              v-model="form.default_model"
              :options="defaultModelOptions"
              :state="fieldErrors.default_model ? false : null"
            />
            <small v-if="fieldErrors.default_model" class="text-danger">{{ fieldErrors.default_model }}</small>
          </div>
          <div>
            <span class="provider-mini-label">Parsing</span>
            <b-form-select
              size="sm"
              v-model="form.parsing_model"
              :options="parsingModelOptions"
              :state="fieldErrors.parsing_model ? false : null"
            />
            <small v-if="fieldErrors.parsing_model" class="text-danger">{{ fieldErrors.parsing_model }}</small>
          </div>
        </div>
      </section>

      <!-- Limits -->
      <section class="provider-section">
        <label class="provider-section__label">Limits</label>
        <div class="provider-grid provider-grid--3">
          <div>
            <span class="provider-mini-label">Timeout (s)</span>
            <b-form-input
              size="sm"
              v-model.number="form.timeout_seconds"
              type="number"
              min="10"
              max="300"
              :state="fieldErrors.timeout_seconds ? false : null"
            />
          </div>
          <div>
            <span class="provider-mini-label">Max file (MB)</span>
            <b-form-input
              size="sm"
              v-model.number="form.max_file_size_mb"
              type="number"
              min="1"
              max="50"
              :state="fieldErrors.max_file_size_mb ? false : null"
            />
          </div>
          <div>
            <span class="provider-mini-label">Max parse chars</span>
            <b-form-input
              size="sm"
              v-model.number="form.max_parse_chars"
              type="number"
              min="1000"
              max="200000"
              :state="fieldErrors.max_parse_chars ? false : null"
            />
          </div>
        </div>
        <small v-if="limitsError" class="text-danger d-block mt-1">{{ limitsError }}</small>
      </section>

      <section class="provider-section">
        <label class="provider-section__label">Vision (image grading) limits</label>
        <div class="provider-grid provider-grid--3">
          <div>
            <span class="provider-mini-label">Max images</span>
            <b-form-input
              size="sm"
              v-model.number="form.max_vision_images"
              type="number"
              min="1"
              max="32"
              :state="fieldErrors.max_vision_images ? false : null"
            />
            <small class="provider-help">Maximum images sent in one grading call.</small>
          </div>
          <div>
            <span class="provider-mini-label">Max scanned PDF pages</span>
            <b-form-input
              size="sm"
              v-model.number="form.max_vision_pages"
              type="number"
              min="1"
              max="16"
              :state="fieldErrors.max_vision_pages ? false : null"
            />
            <small class="provider-help">Maximum PDF pages rasterized for scanned-document grading.</small>
          </div>
          <div>
            <span class="provider-mini-label">Max image dimension (px)</span>
            <b-form-input
              size="sm"
              v-model.number="form.max_vision_image_dim_px"
              type="number"
              min="256"
              max="4096"
              :state="fieldErrors.max_vision_image_dim_px ? false : null"
            />
            <small class="provider-help">Each image's longest edge is downsized to this many pixels.</small>
          </div>
        </div>
        <small v-if="visionLimitsError" class="text-danger d-block mt-1">{{ visionLimitsError }}</small>
      </section>

      <!-- State -->
      <section class="provider-section">
        <label class="provider-section__label">Status</label>
        <div class="provider-state-row">
          <b-form-checkbox v-model="form.is_enabled" switch class="provider-state-row__switch">
            Enabled
          </b-form-checkbox>
          <span class="provider-state-row__sep" aria-hidden="true">·</span>
          <span class="provider-state-row__active">
            <i class="material-icons icon-14pt mr-1" :class="provider.is_active ? 'text-success' : 'text-muted'">
              {{ provider.is_active ? 'check_circle' : 'radio_button_unchecked' }}
            </i>
            {{ provider.is_active ? 'Active' : 'Inactive' }}
          </span>
        </div>
        <small v-if="fieldErrors.is_active" class="text-danger d-block mt-1">{{ fieldErrors.is_active }}</small>
        <small v-else-if="activateBlockedReason && !provider.is_active" class="provider-help">
          {{ activateBlockedReason }}.
        </small>
      </section>

      <!-- Notes -->
      <section class="provider-section">
        <label class="provider-section__label">Notes</label>
        <b-form-textarea
          v-model="form.notes"
          rows="2"
          size="sm"
          placeholder="Internal notes (superadmins only)"
          :state="fieldErrors.notes ? false : null"
        />
      </section>
    </div>

    <!-- Footer (aligned across cards) -->
    <div class="provider-card__footer">
      <div>
        <b-btn
          v-if="!provider.is_active"
          size="sm"
          variant="success"
          :disabled="!canActivate || isActivating"
          @click="onActivate"
        >
          <b-spinner v-if="isActivating" small class="mr-1"></b-spinner>
          <i v-else class="material-icons icon-16pt mr-1">check_circle</i>
          Make active
        </b-btn>
        <b-btn
          v-else
          size="sm"
          variant="outline-warning"
          :disabled="isActivating"
          @click="onDeactivate"
        >
          <b-spinner v-if="isActivating" small class="mr-1"></b-spinner>
          <i v-else class="material-icons icon-16pt mr-1">power_settings_new</i>
          Deactivate
        </b-btn>
      </div>
      <b-btn size="sm" variant="primary" :disabled="isSaving || !isDirty" @click="onSubmit">
        <b-spinner v-if="isSaving" small class="mr-1"></b-spinner>
        <i v-else class="material-icons icon-16pt mr-1">save</i>
        Save changes
      </b-btn>
    </div>
  </div>
</template>

<script>
const PROVIDER_LABELS = {
  openai: 'OpenAI',
  anthropic: 'Anthropic Claude',
};

// Selectable model strings per provider. The `value` is the exact model id the
// backend expects to be stored.
const PROVIDER_MODELS = {
  openai: [
    'gpt-5.6-sol',
    'gpt-5.6-terra',
    'gpt-5.6-luna',
    'gpt-5.5',
    'gpt-5.4',
    'gpt-5.3',
    'gpt-5.2',
    'gpt-5.1',
    'gpt-5',
    'gpt-4.1',
    'gpt-4.1-mini',
    'gpt-4.1-nano',
    'gpt-4o',
    'gpt-4o-mini',
  ],
  anthropic: [
    'claude-fable-5',
    'claude-opus-4-8',
    'claude-opus-4-7',
    'claude-opus-4-6',
    'claude-sonnet-5',
    'claude-sonnet-4-6',
    'claude-sonnet-4-5',
    'claude-opus-4-5',
    'claude-opus-4-1',
    'claude-haiku-4-5',
  ],
};

export default {
  name: 'ProviderCard',
  props: {
    provider: { type: Object, required: true },
    isSaving: { type: Boolean, default: false },
    isActivating: { type: Boolean, default: false },
    serverErrors: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      form: this.buildForm(this.provider),
    };
  },
  watch: {
    provider: {
      handler(val) {
        this.form = this.buildForm(val);
      },
      deep: true,
    },
  },
  computed: {
    providerLabel() {
      return this.provider.provider_display
        || PROVIDER_LABELS[this.provider.provider]
        || this.provider.provider;
    },
    modelChoices() {
      return PROVIDER_MODELS[this.provider.provider] || [];
    },
    defaultModelOptions() {
      // Include the saved value even if it isn't in the known list so an
      // existing custom model id still renders as the selected option.
      const values = [...this.modelChoices];
      if (this.form.default_model && !values.includes(this.form.default_model)) {
        values.unshift(this.form.default_model);
      }
      return [
        { value: '', text: 'Select a model', disabled: true },
        ...values.map(m => ({ value: m, text: m })),
      ];
    },
    parsingModelOptions() {
      const values = [...this.modelChoices];
      if (this.form.parsing_model && !values.includes(this.form.parsing_model)) {
        values.unshift(this.form.parsing_model);
      }
      return [
        { value: '', text: 'None (optional)' },
        ...values.map(m => ({ value: m, text: m })),
      ];
    },
    badge() {
      const p = this.provider;
      if (p.is_active && p.is_enabled) return { text: 'Active', variant: 'success', tone: 'success' };
      if (p.is_enabled) return { text: 'Enabled', variant: 'info', tone: 'info' };
      return { text: 'Disabled', variant: 'secondary', tone: 'muted' };
    },
    apiKeyPlaceholder() {
      if (this.provider.api_key_masked) return this.provider.api_key_masked;
      if (this.provider.has_api_key) return '••••••••';
      return 'Enter API key';
    },
    canActivate() {
      return !!this.provider.is_enabled && !!this.provider.has_api_key;
    },
    activateBlockedReason() {
      if (!this.provider.is_enabled) return 'Enable this provider first';
      if (!this.provider.has_api_key) return 'Add an API key first';
      return '';
    },
    fieldErrors() {
      const out = {};
      const src = this.serverErrors || {};
      Object.keys(src).forEach(k => {
        const v = src[k];
        if (Array.isArray(v)) out[k] = v[0];
        else if (typeof v === 'string') out[k] = v;
      });
      return out;
    },
    limitsError() {
      return this.fieldErrors.timeout_seconds
        || this.fieldErrors.max_file_size_mb
        || this.fieldErrors.max_parse_chars
        || '';
    },
    visionLimitsError() {
      return this.fieldErrors.max_vision_images
        || this.fieldErrors.max_vision_pages
        || this.fieldErrors.max_vision_image_dim_px
        || '';
    },
    serverNonFieldError() {
      const src = this.serverErrors || {};
      const v = src.non_field_errors || src.detail;
      if (Array.isArray(v)) return v[0];
      return v || '';
    },
    isDirty() {
      const p = this.provider;
      return !!this.form.api_key
        || this.form.default_model !== (p.default_model || '')
        || this.form.parsing_model !== (p.parsing_model || '')
        || this.form.timeout_seconds !== p.timeout_seconds
        || this.form.max_file_size_mb !== p.max_file_size_mb
        || this.form.max_parse_chars !== p.max_parse_chars
        || this.form.max_vision_images !== p.max_vision_images
        || this.form.max_vision_pages !== p.max_vision_pages
        || this.form.max_vision_image_dim_px !== p.max_vision_image_dim_px
        || !!this.form.is_enabled !== !!p.is_enabled
        || (this.form.notes || '') !== (p.notes || '');
    },
  },
  methods: {
    buildForm(p) {
      return {
        api_key: '',
        default_model: p.default_model || '',
        parsing_model: p.parsing_model || '',
        timeout_seconds: p.timeout_seconds == null ? 60 : p.timeout_seconds,
        max_file_size_mb: p.max_file_size_mb == null ? 10 : p.max_file_size_mb,
        max_parse_chars: p.max_parse_chars == null ? 60000 : p.max_parse_chars,
        max_vision_images: p.max_vision_images == null ? 8 : p.max_vision_images,
        max_vision_pages: p.max_vision_pages == null ? 4 : p.max_vision_pages,
        max_vision_image_dim_px: p.max_vision_image_dim_px == null ? 1600 : p.max_vision_image_dim_px,
        is_enabled: !!p.is_enabled,
        notes: p.notes || '',
      };
    },
    onSubmit() {
      const p = this.provider;
      const payload = {};
      if (this.form.api_key) payload.api_key = this.form.api_key;
      if (this.form.default_model !== (p.default_model || '')) payload.default_model = this.form.default_model;
      if (this.form.parsing_model !== (p.parsing_model || '')) payload.parsing_model = this.form.parsing_model;
      if (this.form.timeout_seconds !== p.timeout_seconds) payload.timeout_seconds = this.form.timeout_seconds;
      if (this.form.max_file_size_mb !== p.max_file_size_mb) payload.max_file_size_mb = this.form.max_file_size_mb;
      if (this.form.max_parse_chars !== p.max_parse_chars) payload.max_parse_chars = this.form.max_parse_chars;
      if (this.form.max_vision_images !== p.max_vision_images) payload.max_vision_images = this.form.max_vision_images;
      if (this.form.max_vision_pages !== p.max_vision_pages) payload.max_vision_pages = this.form.max_vision_pages;
      if (this.form.max_vision_image_dim_px !== p.max_vision_image_dim_px) {
        payload.max_vision_image_dim_px = this.form.max_vision_image_dim_px;
      }
      if (!!this.form.is_enabled !== !!p.is_enabled) payload.is_enabled = this.form.is_enabled;
      if ((this.form.notes || '') !== (p.notes || '')) payload.notes = this.form.notes;

      if (Object.keys(payload).length === 0) {
        this.$emit('no-op');
        return;
      }
      this.$emit('save', { id: p.id, payload });
    },
    onActivate() {
      this.$emit('activate', { id: this.provider.id, providerLabel: this.providerLabel });
    },
    onDeactivate() {
      this.$emit('deactivate', { id: this.provider.id, providerLabel: this.providerLabel });
    },
  },
};
</script>

<style scoped>
.provider-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.provider-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  background: #fafbfc;
  border-bottom: 1px solid #eef0f3;
}
.provider-card__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.dot-success { background: #10b981; box-shadow: 0 0 0 3px rgba(16, 185, 129, .15); }
.dot-info    { background: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, .15); }
.dot-muted   { background: #9ca3af; }

.provider-card__status-badge {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 0.35rem 0.65rem;
}

.provider-card__body {
  padding: 1rem 1rem 0.25rem;
  flex: 1 1 auto;
}

.provider-section + .provider-section { margin-top: 0.9rem; }
.provider-section__label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.4rem;
}

.provider-mini-label {
  display: block;
  font-size: 0.7rem;
  color: #6b7280;
  margin-bottom: 0.2rem;
}

.provider-grid { display: grid; gap: 0.6rem; }
.provider-grid--2 { grid-template-columns: 1fr 1fr; }
.provider-grid--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }

.provider-help {
  display: block;
  margin-top: 0.25rem;
  color: #6b7280;
  font-size: 0.75rem;
}

.provider-state-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.85rem;
}
.provider-state-row__sep { color: #d1d5db; }
.provider-state-row__active {
  display: inline-flex;
  align-items: center;
  color: #374151;
}
.provider-state-row__active .icon-14pt {
  font-size: 14pt;
  line-height: 1;
}

.provider-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fafbfc;
  border-top: 1px solid #eef0f3;
  margin-top: 0.5rem;
}
</style>
